import dayjs, { Dayjs } from "dayjs"
import { useQuery } from "@/api/utils/useQuery"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { HasuraQueries } from "@/api/hasura/queries"
import { GetActiveDirectDebitAccountsQuery } from "@/api/hasura/generated/graphql"
import {
  getDrawWinners,
  listDraws,
} from "@/api/backofficeRest/generated/loyalty/loyalty"
import {
  DrawDetailResponse,
  ListDrawsResponse,
  PrizeDrawEntryWinner,
  PrizeDrawEntryWinnerStatus,
  PrizeDrawStatus,
} from "@/api/backofficeRest/generated/model"
import { QueryResult } from "@/api/utils/types"
import { DrawParser } from "@/models/operations/draw/draw.parser"
import { DrawDetailParser } from "@/models/operations/draw/drawDetail.parser"
import { DrawWinnerParser } from "@/models/operations/draw/drawWinner.parser"
import { IDraw } from "@/models/operations/draw/draw.types"
import { IDrawDetail } from "@/models/operations/draw/drawDetail.types"
import { IDrawWinner } from "@/models/operations/draw/drawWinner.types"
import { DEFAULT_QUERY_STALE_TIME } from "@/constants/general"

// Poll cadence while the async close+select runs (selection takes seconds–minutes).
const STATUS_POLL_INTERVAL_MS = 3000

type PriorWin = { completionDateTime: string; winners: PrizeDrawEntryWinner[] }

type PreviousWinsRaw = {
  current: PrizeDrawEntryWinner[]
  priorWins: PriorWin[]
}

async function fetchPreviousWins(currentDrawId: string): Promise<PriorWin[]> {
  const { draws } = await listDraws()
  const currentCompletion = draws.find(
    (draw) => String(draw.id) === currentDrawId,
  )?.completionDateTime
  const priorDraws = draws.filter(
    (draw) =>
      String(draw.id) !== currentDrawId &&
      (!currentCompletion ||
        dayjs(draw.completionDateTime).isBefore(dayjs(currentCompletion))),
  )
  // allSettled: one unreachable old draw degrades its accounts to no date,
  // rather than failing the whole winners table.
  const results = await Promise.allSettled(
    priorDraws.map((draw) =>
      getDrawWinners(Number(draw.id)).then((winners) => ({
        completionDateTime: draw.completionDateTime,
        winners,
      })),
    ),
  )
  return results.flatMap((result) =>
    result.status === "fulfilled" ? [result.value] : [],
  )
}

class LoyaltyServiceClass {
  getDraws(): QueryResult<IDraw[]> {
    return useQuery({
      ...BackofficeRestQueries.loyalty.listDraws(),
      useRawResponse: true,
      placeholderData: [] as IDraw[],
      parserFn: (data: ListDrawsResponse): IDraw[] =>
        (data?.draws ?? [])
          .filter(Boolean)
          .map((draw) => DrawParser.toDomain(draw)),
    })
  }

  getDrawDetail(id: string): QueryResult<IDrawDetail | null> {
    return useQuery({
      ...BackofficeRestQueries.loyalty.getDrawDetail(id),
      useRawResponse: true,
      placeholderData: null,
      parserFn: (data: DrawDetailResponse): IDrawDetail | null =>
        data ? DrawDetailParser.toDomain(data) : null,
    })
  }

  getDrawWinners(id: string): QueryResult<IDrawWinner[]> {
    return useQuery({
      ...BackofficeRestQueries.loyalty.getDrawWinners(id),
      useRawResponse: true,
      placeholderData: [] as IDrawWinner[],
      parserFn: (data: PrizeDrawEntryWinner[]): IDrawWinner[] =>
        (data ?? [])
          .filter(Boolean)
          .map((winner) => DrawWinnerParser.toDomain(winner)),
    })
  }

  /**
   * Winners of a draw, each annotated with the completion date of the most
   * recent earlier draw the same billing account won (lastWonDate). Prior
   * draws are immutable once completed, so the fan-out caches cleanly.
   */
  getDrawWinnersWithPreviousWins(id: string): QueryResult<IDrawWinner[]> {
    return useQuery({
      queryKey: ["drawWinnersWithPreviousWins", id],
      useRawResponse: true,
      placeholderData: [] as IDrawWinner[],
      queryFn: async (): Promise<PreviousWinsRaw> => {
        // Current winners are the core data (hard-fail); the previous-win
        // annotations are supplementary, so their fetch failures degrade to no
        // dates rather than blanking the table.
        const [current, priorWins] = await Promise.all([
          getDrawWinners(Number(id)),
          fetchPreviousWins(id).catch(() => [] as PriorWin[]),
        ])
        return { current, priorWins }
      },
      parserFn: (raw: PreviousWinsRaw): IDrawWinner[] => {
        const lastWonByAccount = new Map<string, Dayjs>()
        for (const { completionDateTime, winners } of raw.priorWins) {
          const date = dayjs(completionDateTime)
          for (const winner of winners ?? []) {
            if (winner.status === PrizeDrawEntryWinnerStatus.REJECTED) continue
            const key = String(winner.billingAccountId)
            const existing = lastWonByAccount.get(key)
            if (!existing || date.isAfter(existing)) {
              lastWonByAccount.set(key, date)
            }
          }
        }
        return (raw.current ?? [])
          .filter(Boolean)
          .map((winner) =>
            DrawWinnerParser.toDomain(
              winner,
              lastWonByAccount.get(String(winner.billingAccountId)) ?? null,
            ),
          )
      },
    })
  }

  /**
   * Whether each billing account currently has an active Direct Debit, keyed
   * by billingAccountId. One query covers every account; "active" is defined
   * in GetActiveDirectDebitAccounts.graphql.
   */
  getDirectDebitStatuses(
    billingAccountIds: string[],
  ): QueryResult<Record<string, boolean>> {
    const accountIds = [...new Set(billingAccountIds)].sort((a, b) =>
      a.localeCompare(b),
    )
    return useQuery({
      ...HasuraQueries.account.getActiveDirectDebitAccounts(accountIds),
      useRawResponse: true,
      placeholderData: {},
      enabled: accountIds.length > 0,
      staleTime: DEFAULT_QUERY_STALE_TIME,
      parserFn: (raw: GetActiveDirectDebitAccountsQuery) => {
        const active = new Set(
          (raw?.payment_method ?? []).map((m) => String(m.billing_account_id)),
        )
        return Object.fromEntries(
          accountIds.map((accountId) => [accountId, active.has(accountId)]),
        )
      },
    })
  }

  getDrawStatus(id: string): QueryResult<PrizeDrawStatus | null> {
    return useQuery({
      ...BackofficeRestQueries.loyalty.getDrawStatus(id),
      useRawResponse: true,
      placeholderData: null,
      // Poll only while the async selection is running; vue-query stops the
      // interval automatically once the status settles (or errors).
      refetchInterval: (query) =>
        query.state.status !== "error" &&
        query.state.data === PrizeDrawStatus.CALCULATING_WINNERS
          ? STATUS_POLL_INTERVAL_MS
          : false,
      parserFn: (data: DrawDetailResponse): PrizeDrawStatus | null =>
        data?.status ?? null,
    })
  }
}

export const LoyaltyService = new LoyaltyServiceClass()
